// src/utils/queryClient.ts
import {
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
  Mutation,
} from "@tanstack/react-query";

import { snackActions } from "./SnackbarUtils";

// --- Handlers for Query Client ---

const handleMutationError = (
  error: any,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, any, unknown>
) => {
  if (mutation.meta?.skipGlobalError) return;
  snackActions.error(error.message);
};

const handleQueryError = (error: any, query: Query<unknown, any>) => {
  if (query.meta?.skipGlobalError) return;
  snackActions.error(error.message);
};

const handleSuccess = (
  response: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>
) => {
  if (mutation.meta?.skipGlobalSuccess) return;
  if (
    response &&
    typeof response === "object" &&
    "message" in response &&
    response.message !== ""
  ) {
    snackActions.success((response as { message: string }).message);
  }
};

// --- Factory and Initialization ---

// Create a factory function for the query client
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
      onSuccess: handleSuccess,
    }),
  });
};

// This will hold our singleton client instance
export let queryClient: QueryClient;

// This function creates and assigns the client instance.
// It's safe to call multiple times, but will only create the client once.
export const initializeQueryClient = () => {
  if (!queryClient) {
    console.log("Initializing Query Client...");
    queryClient = createQueryClient();
  }
  return queryClient;
};
